import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiPlus, FiEdit2, FiSave, FiFileText } from 'react-icons/fi';
import { procesosService } from '../../services';
import { LoadingGrid } from '../../components/UI/Loading';
import './ProcesosList.scss';

/**
 * Vista de lista de procesos con edición inline
 * Basado en el diseño del PDF del cliente (páginas 2-7)
 */
const ProcesosList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const processType = location.state?.processType || 'ENCAPSULATION';

  const [procesos, setProcesos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [generalNotes, setGeneralNotes] = useState('');
  const [newRow, setNewRow] = useState(null);

  useEffect(() => {
    loadProcesos();
  }, [processType]);

  const loadProcesos = async () => {
    setIsLoading(true);
    try {
      const result = await procesosService.getByType(processType, { limit: 100 });
      if (result.success) {
        setProcesos(result.data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const getPageTitle = () => {
    const titles = {
      'ENCAPSULATION': 'REGISTROS\nDE ENCAPSULADO',
      'TEA_PACKING': 'REGISTROS\nDE TÉS',
      'PACKAGING': 'REGISTROS\nDE EMPACADO',
      'CLEANING': 'REGISTROS\nDE LIMPIEZA',
      'RECEIVING': 'REGISTROS\nPARA RECIBIR\nPRODUCTOS',
      'RETURNS': 'REGISTROS DE\nRETORNO DE\nPRODUCTOS',
      'MAINTENANCE': 'REGISTRO\nDE MANTENIMIENTO'
    };
    return titles[processType] || 'REGISTROS';
  };

  const handleAddNew = () => {
    const emptyRow = createEmptyRow(processType);
    setNewRow(emptyRow);
    setEditingId('new');
  };

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleSave = async (proceso) => {
    try {
      let result;
      if (proceso.id === 'new') {
        result = await procesosService.createProceso({
          process_type: processType,
          production_process: {
            process_type: processType,
            process_date: proceso.process_date || new Date().toISOString().split('T')[0],
            product_name: proceso.product_name || '',
            operator_name: proceso.operator_name || '',
            notes: generalNotes
          },
          process_details: proceso
        });
        setNewRow(null);
      } else {
        result = await procesosService.updateProceso(proceso.id, proceso);
      }

      if (result.success) {
        loadProcesos();
        setEditingId(null);
      }
    } catch (err) {
      console.error('Error saving:', err);
    }
  };

  const handleDownloadReport = () => {
    console.log('Descargar reporte de', processType);
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando registros..." />;
  }

  return (
    <div className="procesos-list-page">
      {/* Header */}
      <div className="procesos-list-header">
        <div>
          <h1 className="procesos-list-title">
            {getPageTitle().split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < getPageTitle().split('\n').length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
        </div>
        <div className="procesos-list-actions">
          <button
            className="btn-link-purple"
            onClick={() => navigate('/system/procesos')}
          >
            REGRESAR AL MENÚ
          </button>
          <button
            className="btn-purple-pill"
            onClick={handleDownloadReport}
          >
            <FiFileText size={18} />
            REPORTE
          </button>
        </div>
      </div>

      {/* Notas Generales */}
      <div className="general-notes-section">
        <label className="general-notes-label">NOTAS GENERALES:</label>
        <textarea
          className="general-notes-textarea"
          value={generalNotes}
          onChange={(e) => setGeneralNotes(e.target.value)}
          placeholder="Escribe notas generales sobre este proceso..."
          rows="4"
        />
      </div>

      {/* Tabla según tipo de proceso */}
      <div className="procesos-table-container">
        {renderTableByType(processType, procesos, newRow, editingId, handleEdit, handleSave, handleAddNew)}
      </div>
    </div>
  );
};

// Función auxiliar para crear fila vacía según tipo
const createEmptyRow = (type) => {
  const baseRow = {
    id: 'new',
    product_name: '',
    process_date: new Date().toISOString().split('T')[0],
    operator_name: ''
  };

  switch (type) {
    case 'ENCAPSULATION':
      return {
        ...baseRow,
        net_weight_per_capsule: 0,
        actual_weight_per_capsule: 0,
        bottles_produced: 0
      };
    case 'TEA_PACKING':
    case 'PACKAGING':
      return {
        ...baseRow,
        packed_units: 0,
        batch_number: '',
        company_name: '',
        packer_name: ''
      };
    case 'CLEANING':
      return {
        ...baseRow,
        deep_cleaning: false,
        weekly_cleaning: false,
        production_area: false,
        warehouse_area: false,
        outside_area: false,
        restroom: false
      };
    case 'RECEIVING':
      return {
        ...baseRow,
        received_boxes: 0,
        weight_per_box: 0,
        total_received: 0,
        company_name: '',
        receiver_name: ''
      };
    case 'RETURNS':
      return {
        ...baseRow,
        quantity_returned: 0,
        client_name: '',
        return_status: 'GOOD'
      };
    default:
      return baseRow;
  }
};

// Renderizar tabla según tipo de proceso
const renderTableByType = (type, procesos, newRow, editingId, handleEdit, handleSave, handleAddNew) => {
  const allRows = newRow ? [newRow, ...procesos] : procesos;

  switch (type) {
    case 'ENCAPSULATION':
      return <EncapsulationTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'TEA_PACKING':
      return <TeaPackingTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'PACKAGING':
      return <PackagingTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'CLEANING':
      return <CleaningTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'RECEIVING':
      return <ReceivingTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'RETURNS':
      return <ReturnsTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    case 'MAINTENANCE':
      return <MaintenanceTable rows={allRows} editingId={editingId} onEdit={handleEdit} onSave={handleSave} onAddNew={handleAddNew} />;
    default:
      return <div>Tabla no implementada para tipo: {type}</div>;
  }
};

// Componente de tabla para Encapsulado
const EncapsulationTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>PRODUCT NAME</th>
          <th>PRODUCTION<br/>DATE</th>
          <th>NET WEIGHT<br/>PER CAPSULE</th>
          <th>WEIGHT GIVEN<br/>PER CAPSULE</th>
          <th>QTY PRODUCED<br/>IN BOTTLES</th>
          <th>OPERATOR<br/>NAME</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  row.product_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.net_weight_per_capsule || ''}
                    onChange={(e) => handleFieldChange('net_weight_per_capsule', e.target.value)}
                    placeholder="0 MCG"
                  />
                ) : (
                  `${row.net_weight_per_capsule || 0} MCG`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.actual_weight_per_capsule || ''}
                    onChange={(e) => handleFieldChange('actual_weight_per_capsule', e.target.value)}
                    placeholder="0 MCG"
                  />
                ) : (
                  `${row.actual_weight_per_capsule || 0} MCG`
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.bottles_produced || ''}
                    onChange={(e) => handleFieldChange('bottles_produced', e.target.value)}
                    placeholder="250"
                  />
                ) : (
                  row.bottles_produced || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.operator_name || ''}
                    onChange={(e) => handleFieldChange('operator_name', e.target.value)}
                  />
                ) : (
                  row.operator_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Tés (TEA_PACKING)
const TeaPackingTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>PRODUCT NAME</th>
          <th>PACKING<br/>DATE</th>
          <th>PACKED<br/>UNITS</th>
          <th>BATCH<br/>NUMBER</th>
          <th>COMPANY<br/>NAME</th>
          <th>PACKER<br/>NAME</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  row.product_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.packed_units || ''}
                    onChange={(e) => handleFieldChange('packed_units', e.target.value)}
                  />
                ) : (
                  row.packed_units || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="0000"
                  />
                ) : (
                  row.batch_number || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.packer_name || ''}
                    onChange={(e) => handleFieldChange('packer_name', e.target.value)}
                  />
                ) : (
                  row.packer_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Empacado (PACKAGING)
const PackagingTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>PRODUCT NAME</th>
          <th>PACKING<br/>DATE</th>
          <th>PACKED<br/>UNITS</th>
          <th>BATCH<br/>NUMBER</th>
          <th>COMPANY<br/>NAME</th>
          <th>PACKER<br/>NAME</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  row.product_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.packed_units || ''}
                    onChange={(e) => handleFieldChange('packed_units', e.target.value)}
                  />
                ) : (
                  row.packed_units || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.batch_number || ''}
                    onChange={(e) => handleFieldChange('batch_number', e.target.value)}
                    placeholder="0000"
                  />
                ) : (
                  row.batch_number || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.packer_name || ''}
                    onChange={(e) => handleFieldChange('packer_name', e.target.value)}
                  />
                ) : (
                  row.packer_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Limpieza (CLEANING) - Con checkboxes
const CleaningTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckboxChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table proceso-table--cleaning">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>DATE</th>
          <th>DEEP<br/>CLEANING</th>
          <th>WEEKLY<br/>CLEANING</th>
          <th colSpan="4" className="areas-header">AREAS</th>
          <th>NAME</th>
          <th className="actions-column"></th>
        </tr>
        <tr className="sub-header">
          <th></th>
          <th></th>
          <th></th>
          <th></th>
          <th className="area-column">PRODUCTION</th>
          <th className="area-column">WAREHOUSE</th>
          <th className="area-column">OUTSIDE</th>
          <th className="area-column">RESTROOM</th>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.deep_cleaning || false}
                    onChange={(e) => handleCheckboxChange('deep_cleaning', e.target.checked)}
                  />
                ) : (
                  currentRow.deep_cleaning ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.weekly_cleaning || false}
                    onChange={(e) => handleCheckboxChange('weekly_cleaning', e.target.checked)}
                  />
                ) : (
                  currentRow.weekly_cleaning ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.production_area || false}
                    onChange={(e) => handleCheckboxChange('production_area', e.target.checked)}
                  />
                ) : (
                  currentRow.production_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.warehouse_area || false}
                    onChange={(e) => handleCheckboxChange('warehouse_area', e.target.checked)}
                  />
                ) : (
                  currentRow.warehouse_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.outside_area || false}
                    onChange={(e) => handleCheckboxChange('outside_area', e.target.checked)}
                  />
                ) : (
                  currentRow.outside_area ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.restroom || false}
                    onChange={(e) => handleCheckboxChange('restroom', e.target.checked)}
                  />
                ) : (
                  currentRow.restroom ? <span className="checkmark">✓</span> : ''
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.operator_name || ''}
                    onChange={(e) => handleFieldChange('operator_name', e.target.value)}
                  />
                ) : (
                  row.operator_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Recepción (RECEIVING)
const ReceivingTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>PRODUCT NAME</th>
          <th>DATE</th>
          <th>RECEIVED<br/>BOXES</th>
          <th>WEIGHT<br/>PER BOX</th>
          <th>TOTAL<br/>RECEIVED</th>
          <th>COMPANY</th>
          <th>RECEIVER<br/>NAME</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  row.product_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.received_boxes || ''}
                    onChange={(e) => handleFieldChange('received_boxes', e.target.value)}
                  />
                ) : (
                  row.received_boxes || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.weight_per_box || ''}
                    onChange={(e) => handleFieldChange('weight_per_box', e.target.value)}
                  />
                ) : (
                  row.weight_per_box || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.total_received || ''}
                    onChange={(e) => handleFieldChange('total_received', e.target.value)}
                  />
                ) : (
                  row.total_received || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.company_name || ''}
                    onChange={(e) => handleFieldChange('company_name', e.target.value)}
                  />
                ) : (
                  row.company_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.receiver_name || ''}
                    onChange={(e) => handleFieldChange('receiver_name', e.target.value)}
                  />
                ) : (
                  row.receiver_name || '-'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Retornos (RETURNS)
const ReturnsTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>PRODUCT NAME</th>
          <th>DATE</th>
          <th>QUANTITY<br/>RETURNED</th>
          <th>CLIENT NAME</th>
          <th>PRODUCT STATUS<br/>(BAD OR GOOD)</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.product_name || ''}
                    onChange={(e) => handleFieldChange('product_name', e.target.value)}
                  />
                ) : (
                  row.product_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    className="inline-input inline-input--number"
                    value={currentRow.quantity_returned || ''}
                    onChange={(e) => handleFieldChange('quantity_returned', e.target.value)}
                  />
                ) : (
                  row.quantity_returned || 0
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.client_name || ''}
                    onChange={(e) => handleFieldChange('client_name', e.target.value)}
                  />
                ) : (
                  row.client_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <select
                    className="inline-input"
                    value={currentRow.return_status || 'GOOD'}
                    onChange={(e) => handleFieldChange('return_status', e.target.value)}
                  >
                    <option value="GOOD">GOOD</option>
                    <option value="BAD">BAD</option>
                  </select>
                ) : (
                  row.return_status || 'GOOD'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Tabla para Mantenimiento (MAINTENANCE)
const MaintenanceTable = ({ rows, editingId, onEdit, onSave, onAddNew }) => {
  const [editedRow, setEditedRow] = useState({});

  const handleFieldChange = (field, value) => {
    setEditedRow(prev => ({ ...prev, [field]: value }));
  };

  return (
    <table className="proceso-table">
      <thead>
        <tr>
          <th className="add-column">
            <button className="btn-add-row" onClick={onAddNew}>
              <FiPlus size={20} />
            </button>
          </th>
          <th>EQUIPMENT<br/>NAME</th>
          <th>DATE</th>
          <th>MAINTENANCE<br/>TYPE</th>
          <th>TECHNICIAN<br/>NAME</th>
          <th>PARTS<br/>REPLACED</th>
          <th>TOTAL<br/>COST</th>
          <th>OPERATIONAL</th>
          <th className="actions-column"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const isEditing = editingId === row.id;
          const currentRow = isEditing ? { ...row, ...editedRow } : row;

          return (
            <tr key={row.id} className={isEditing ? 'editing' : ''}>
              <td className="add-column"></td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.equipment_name || ''}
                    onChange={(e) => handleFieldChange('equipment_name', e.target.value)}
                  />
                ) : (
                  row.equipment_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="date"
                    className="inline-input inline-input--date"
                    value={currentRow.process_date || ''}
                    onChange={(e) => handleFieldChange('process_date', e.target.value)}
                  />
                ) : (
                  row.process_date ? new Date(row.process_date).toLocaleDateString() : '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <select
                    className="inline-input"
                    value={currentRow.maintenance_type || 'PREVENTIVE'}
                    onChange={(e) => handleFieldChange('maintenance_type', e.target.value)}
                  >
                    <option value="PREVENTIVE">Preventivo</option>
                    <option value="CORRECTIVE">Correctivo</option>
                    <option value="PREDICTIVE">Predictivo</option>
                    <option value="EMERGENCY">Emergencia</option>
                  </select>
                ) : (
                  row.maintenance_type || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.technician_name || ''}
                    onChange={(e) => handleFieldChange('technician_name', e.target.value)}
                  />
                ) : (
                  row.technician_name || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="text"
                    className="inline-input"
                    value={currentRow.parts_replaced || ''}
                    onChange={(e) => handleFieldChange('parts_replaced', e.target.value)}
                  />
                ) : (
                  row.parts_replaced || '-'
                )}
              </td>
              <td>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    className="inline-input inline-input--number"
                    value={currentRow.total_cost || ''}
                    onChange={(e) => handleFieldChange('total_cost', e.target.value)}
                  />
                ) : (
                  `$${(row.total_cost || 0).toFixed(2)}`
                )}
              </td>
              <td className="text-center">
                {isEditing ? (
                  <input
                    type="checkbox"
                    className="checkbox-large"
                    checked={currentRow.is_operational || false}
                    onChange={(e) => handleFieldChange('is_operational', e.target.checked)}
                  />
                ) : (
                  currentRow.is_operational ? <span className="checkmark">✓</span> : '✗'
                )}
              </td>
              <td className="actions-column">
                {isEditing ? (
                  <button
                    className="btn-save"
                    onClick={() => {
                      onSave(currentRow);
                      setEditedRow({});
                    }}
                  >
                    <FiSave size={16} />
                    GUARDAR
                  </button>
                ) : (
                  <button
                    className="btn-edit"
                    onClick={() => {
                      onEdit(row.id);
                      setEditedRow(row);
                    }}
                  >
                    <FiEdit2 size={16} />
                    EDITAR
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default ProcesosList;
